import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Sparkles, ArrowLeft, Coins, Star, Check, Shield, Infinity, Crown,
  CreditCard, Smartphone, Zap, MessageSquare, Image, Video, Music, Bot, RefreshCw
} from 'lucide-react'
import client from '../api/client'
import { useAuthStore } from '../store/authStore'

interface Plan {
  id: string
  label: string
  tokens: number
  stars: number
  price: number
  price_label: string
  popular?: boolean
  free?: boolean
  tokens_cheapest?: number
}

const FREE_MODELS = [
  'GPT-5.2, GPT-5.1',
  'Claude Sonnet 4, Gemini 2.5 Pro',
  'DeepSeek V4, Grok 4',
]

const PLAN_FEATURES: Record<string, string[]> = {
  free: [
    'Доступ к 6 AI-моделям бесплатно и безлимитно',
    'Голосовой ввод и вывод',
    'Чат',
    'Работа с таблицами и документами',
    'Переводчик, Компрессор текста, YouTube суммаризатор',
    'Уникальные AI-персонажи',
    'AI Помощник по коду',
    'ИИ-Агенты',
  ],
  starter: [
    '7 000 000 CN-токенов',
    '700 ⭐ для генерации медиа',
    'Загрузка и обработка файлов',
    'Поиск в интернете',
    'Deep Research (глубокий анализ)',
    'Голосовой ввод и вывод',
    'Все топовые AI-модели',
    'Генерация картинок, видео и музыки',
    'ИИ-агенты для сложных задач',
  ],
  standard: [
    '14 000 000 CN-токенов',
    '1 400 ⭐ для генерации медиа',
    'Загрузка и обработка файлов',
    'Поиск в интернете',
    'Deep Research (глубокий анализ)',
    'Голосовой ввод и вывод',
    'Все топовые AI-модели',
    'Генерация картинок, видео и музыки',
    'ИИ-агенты для сложных задач',
  ],
  promax: [
    'Подписка на 30 дней с ежемесячным обновлением',
    '23 000 000 CN-токенов каждый месяц',
    '2 300 ⭐ для генерации медиа каждый месяц',
    'Загрузка и обработка файлов',
    'Поиск в интернете',
    'Deep Research (глубокий анализ)',
    'Голосовой ввод и вывод',
    'Все топовые AI-модели',
    'Генерация картинок, видео и музыки',
    'ИИ-агенты для сложных задач',
    'Автоматическое продление',
  ],
  premium: [
    '42 000 000 CN-токенов',
    '4 200 ⭐ для генерации медиа',
    'Загрузка и обработка файлов',
    'Поиск в интернете',
    'Deep Research (глубокий анализ)',
    'Голосовой ввод и вывод',
    'Все топовые AI-модели',
    'Генерация картинок, видео и музыки',
    'ИИ-агенты для сложных задач',
  ],
  max: [
    '70 000 000 CN-токенов',
    '7 000 ⭐ для генерации медиа',
    'Загрузка и обработка файлов',
    'Поиск в интернете',
    'Deep Research (глубокий анализ)',
    'Голосовой ввод и вывод',
    'Все топовые AI-модели',
    'Генерация картинок, видео и музыки',
    'ИИ-агенты для сложных задач',
  ],
}

export default function Pricing() {
  const [plans, setPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState(true)
  const { user } = useAuthStore()
  const navigate = useNavigate()

  useEffect(() => {
    client.get('/tokens/plans')
      .then(({ data }) => {
        setPlans(data.plans ?? [])
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  const handleChoose = (plan: Plan) => {
    if (!user) {
      navigate('/register')
      return
    }
    if (plan.free || plan.price === 0) {
      navigate('/chat')
      return
    }
    navigate('/payment')
  }

  const tokensPerRub = (plan: Plan) => plan.price > 0 ? (plan.tokens / plan.price).toFixed(0) : '—'

  const freePlan = plans.find(p => (p as any).free)
  const paidPlans = plans.filter(p => !(p as any).free)

  return (
    <div>

      <div className="max-w-6xl mx-auto px-4 pt-4 pb-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <Link to="/" className="p-2 rounded-xl glass-hover text-gray-400 hover:text-white transition-all whitespace-nowrap">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold gradient-text">Тарифы Chat-Neuro.ru</h1>
              <p className="text-xs text-gray-500">
                Выберите тариф и получите доступ ко всем AI-возможностям
              </p>
            </div>
          </div>
        </div>

        {/* Features bar */}
        <div className="flex flex-wrap gap-2 mt-6 mb-8 text-xs text-gray-400">
          <span className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/5 flex items-center gap-1.5">
            <Check className="w-3 h-3 text-green-400" /> Бесплатные модели безлимитно
          </span>
          <span className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/5 flex items-center gap-1.5">
            <Check className="w-3 h-3 text-green-400" /> 30+ AI-моделей
          </span>
          <span className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/5 flex items-center gap-1.5">
            <Check className="w-3 h-3 text-green-400" /> ChatGPT, Claude, Gemini, DeepSeek
          </span>
          <span className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/5 flex items-center gap-1.5">
            <Check className="w-3 h-3 text-green-400" /> Изображения, видео, музыка
          </span>
          <span className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/5 flex items-center gap-1.5">
            <Check className="w-3 h-3 text-green-400" /> Веб-поиск в чате
          </span>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Бесплатный тариф */}
            {freePlan && (
              <div className="glass-card p-5 transition-all border border-green-500/20 bg-gradient-to-b from-green-900/10 to-transparent flex flex-col">
                <div className="pt-2">
                  <div className="w-10 h-10 mx-auto mb-4 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                    <Infinity className="w-5 h-5 text-emerald-400" />
                  </div>
                  <h2 className="text-xl font-bold text-white text-center">{freePlan.label}</h2>
                  <p className="text-[10px] text-gray-500 mt-0.5 text-center">Всегда бесплатно, без ограничений</p>
                  <div className="mt-4 mb-4 text-center">
                    <p className="text-lg font-bold text-green-400">Безлимитные запросы</p>
                    <p className="text-[10px] text-gray-500">к 6 бесплатным AI-моделям</p>
                  </div>
                  <div className="mb-4 text-center">
                    <p className="text-3xl font-bold text-emerald-400">0 ₽</p>
                  </div>
                  <div className="text-xs text-gray-400 space-y-1.5 bg-white/[0.02] rounded-xl p-3 mb-4">
                    <p className="font-medium text-gray-300 mb-1.5">Доступные модели:</p>
                    {FREE_MODELS.map((m, i) => (
                      <p key={i} className="flex items-center gap-1.5">
                        <Check className="w-3 h-3 text-emerald-400 shrink-0" />
                        {m}
                      </p>
                    ))}
                  </div>
                  <div className="space-y-1.5 text-xs text-gray-500 mb-4">
                    {(PLAN_FEATURES.free || []).map((f, i) => (
                      <p key={i} className="flex items-center gap-2">
                        <Check className="w-3 h-3 text-green-400 shrink-0" />
                        {f}
                      </p>
                    ))}
                  </div>
                </div>
                <div className="flex-1" />
                <button
                  onClick={() => handleChoose(freePlan)}
                  className="w-full py-2.5 rounded-xl bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 border border-emerald-500/20 text-sm font-semibold transition-all"
                >
                  Начать бесплатно
                </button>
              </div>
            )}

            {/* Платные тарифы */}
            {paidPlans.map((plan) => {
              const starsCount = Math.floor(plan.tokens / 10000)
              const features = PLAN_FEATURES[plan.id] || []
              return (
                <div
                  key={plan.id}
                  className={`glass-card p-5 transition-all flex flex-col ${
                    plan.popular
                      ? 'ring-2 ring-amber-500/50 shadow-lg shadow-amber-500/10'
                      : ''
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="px-3 py-0.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold shadow-lg">
                        Популярный
                      </span>
                    </div>
                  )}

                  <div className="text-center pt-2">
                    <div className={`w-10 h-10 mx-auto mb-4 rounded-xl flex items-center justify-center ${
                      plan.popular ? 'bg-amber-500/10' : 'bg-white/5'
                    }`}>
                      <Crown className={`w-5 h-5 ${plan.popular ? 'text-amber-400' : 'text-amber-400'}`} />
                    </div>
                    <h2 className="text-xl font-bold text-white">{plan.label}</h2>
                    {plan.id !== 'promax' && (
                      <p className="text-[10px] text-gray-500 mt-0.5">Разовое пополнение</p>
                    )}
                    {plan.id === 'promax' && (
                      <p className="text-[10px] text-sky-400 mt-0.5">🔄 Ежемесячная подписка</p>
                    )}

                    <div className="mt-4 mb-4">
                      <p className="text-2xl font-bold text-amber-300">{plan.tokens.toLocaleString()}</p>
                      <p className="text-[10px] text-gray-500">CN-токенов</p>
                      {starsCount > 0 && (
                        <p className="text-sm text-indigo-300 font-semibold mt-1">
                          или ✦ {starsCount.toLocaleString()} звёзд
                          <span className="text-[10px] text-indigo-400/70 block mt-0.5">
                            (для генерации изображений, видео и музыки)
                          </span>
                        </p>
                      )}
                    </div>

                    <div className="mb-4">
                      <p className="text-3xl font-bold text-white">
                        {plan.price.toLocaleString()} <span className="text-base text-gray-500">₽</span>
                      </p>
                      <p className="text-[10px] text-gray-500 mt-0.5">
                        За 1 ₽ — {tokensPerRub(plan)} токенов
                      </p>
                    </div>

                    {/* Сервисы, которые покрывает тариф */}
                    <div className="flex justify-center gap-3 mb-4 text-[10px] text-gray-400">
                      <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Чат</span>
                      <span className="flex items-center gap-1"><Image className="w-3 h-3" /> Изображения</span>
                      <span className="flex items-center gap-1"><Video className="w-3 h-3" /> Видео</span>
                      <span className="flex items-center gap-1"><Music className="w-3 h-3" /> Музыка</span>
                    </div>

                    {/* Features */}
                    <div className="space-y-1.5 text-xs text-gray-500 mb-4 text-left">
                      {features.map((f, i) => (
                        <p key={i} className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-green-400 shrink-0" />
                          {f}
                        </p>
                      ))}
                    </div>
                  </div>

                  <div className="flex-1" />

                  <button
                    onClick={() => handleChoose(plan)}
                    className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-all ${
                      plan.popular
                        ? 'gradient-btn'
                        : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10'
                    }`}
                  >
                    {user ? 'Пополнить' : 'Выбрать тариф'}
                  </button>
                </div>
              )
            })}
          </div>
        )}

        {/* Курс ЦБ РФ */}
        <CurrencyRate />

        {/* Bottom info */}
        <div className="mt-12 glass-card p-6 text-center">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            {[
              { icon: Shield, label: 'Безопасная оплата', desc: 'Все платежи через ЮKassa защищены' },
              { icon: CreditCard, label: 'Мгновенное зачисление', desc: 'Токены начисляются сразу после оплаты' },
              { icon: Smartphone, label: 'Доступно 24/7', desc: 'Пополняйте баланс в любое время' },
            ].map((f) => (
              <div key={f.label} className="flex items-start gap-3 p-3 rounded-xl glass-hover text-left">
                <f.icon className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-200">{f.label}</p>
                  <p className="text-xs text-gray-500">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500">
            По всем вопросам:{' '}
            <a href="mailto:support@chat-neuro.ru" className="text-indigo-400 hover:text-indigo-300">support@chat-neuro.ru</a>
          </p>
        </div>
      </div>


    </div>
  )
}

{/* ─────────────────────── Курс ЦБ РФ ─────────────────────── */}
function CurrencyRate() {
  const [rate, setRate] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const fetchRate = async () => {
    setLoading(true)
    setError(false)
    try {
      const { data } = await client.get('/admin/ai-models/usd-rate')
      setRate(data.rate)
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRate()
  }, [])

  const starPrice = rate ? (10000 / 1000000 * rate).toFixed(2) : '—'

  return (
    <div className="mt-6 mb-4">
      <div className="glass-card p-5 rounded-2xl">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-300">Расчёт токенов</h3>
        </div>
        {loading ? (
          <div className="space-y-2">
            <div className="h-3 w-64 bg-gray-700/50 rounded animate-pulse" />
            <div className="h-3 w-72 bg-gray-700/50 rounded animate-pulse" />
            <div className="h-3 w-48 bg-gray-700/50 rounded animate-pulse" />
          </div>
        ) : error ? (
          <p className="text-xs text-red-400">Не удалось загрузить курс. Попробуйте обновить.</p>
        ) : (
          <ul className="text-xs text-gray-500 space-y-1.5">
            <li>· 1 000 000 CN ≈ {rate ? rate.toFixed(2) : '—'} ₽ (1 USD по курсу ЦБ РФ).</li>
            <li>· 1 ⭐ ≈ {starPrice} ₽ (1 ⭐ = 10 000 CN) — для изображений, видео и музыки</li>
            <li>· CN-токены и ✦ начисляются при пополнении баланса.</li>
            <li>· Токены не сгорают.</li>
          </ul>
        )}
      </div>
    </div>
  )
}
